// FRU-24201 (ANGULAR-7F7): a step or answer post that still got no answer (status 0) after its resends (FRU-24080) was reported
// to Sentry and lost from the conversation record. Those are phones whose connection dropped: on the same day Cloudflare answered
// 156,154 of these posts and logged 217 as closed by the browser (499). The post is now kept for the tab (sessionStorage) and sent
// when the connection comes back (online, the page shown again, or the next page in the tab). Only one that still can't be sent is
// reported. A resent post carries meta.resent = 1, since the first one may have reached the server before the phone hung up.

/** A step or answer post waiting for the connection, with when it was kept and how many sends of it got no answer since. */
export interface OutboxItem {
  post: any;
  queuedAt: number;
  tries: number;
}

export interface OutboxStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const ACTION_OUTBOX_KEY = 'hope_action_outbox_v1';
/** The tab keeps at most this many unsent posts; past it the oldest go first. */
export const ACTION_OUTBOX_MAX_ITEMS = 20;
/** A kept post is given up (and reported) after this many sends that got no answer... */
export const ACTION_OUTBOX_MAX_TRIES = 3;
/** ...or once it has waited this long. */
export const ACTION_OUTBOX_MAX_AGE_MS = 30 * 60 * 1000;

let testStore: OutboxStore | null | undefined;

/**
 * Page-wide guards. The app and Hope's lazily created module each have their own FruHttpService, and both resume the same
 * stored posts, so listening and sending are guarded once per page, not per service: otherwise each kept post could go twice.
 */
export const actionOutboxGuards = { wired: false, flushing: false };

/** Test-only: keep posts in the given store instead of sessionStorage (undefined restores it), and reset the page guards so
 *  the next service listens on the test's own targets. */
export function setActionOutboxStoreForTests(store: OutboxStore | null | undefined): void {
  testStore = store;
  actionOutboxGuards.wired = false;
  actionOutboxGuards.flushing = false;
}

/** The tab's sessionStorage, or null where it can't be reached. Nothing is written here: a store that refuses writes is found by
 *  addToOutbox reading its write back, so a page with nothing kept never writes to storage. */
export function defaultOutboxStore(): OutboxStore | null {
  if (testStore !== undefined) { return testStore; }
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

export function readOutbox(store: OutboxStore | null): OutboxItem[] {
  if (!store) { return []; }
  try {
    const parsed = JSON.parse(store.getItem(ACTION_OUTBOX_KEY) || '[]');
    if (!Array.isArray(parsed)) { return []; }
    return parsed.filter((item: any) => !!item && typeof item === 'object' && !!item.post && typeof item.post === 'object'
      && typeof item.queuedAt === 'number' && typeof item.tries === 'number');
  } catch (e) {
    return [];
  }
}

/** Save the kept posts. False when the store refused the write (full or blocked). */
export function writeOutbox(store: OutboxStore | null, items: OutboxItem[]): boolean {
  if (!store) { return false; }
  try {
    if (items.length) {
      store.setItem(ACTION_OUTBOX_KEY, JSON.stringify(items));
    } else {
      store.removeItem(ACTION_OUTBOX_KEY);
    }
    return true;
  } catch (e) {
    return false;
  }
}

/** Whether two kept entries are the same post kept at the same time. */
export function sameOutboxItem(a: OutboxItem, b: OutboxItem): boolean {
  return a.queuedAt === b.queuedAt && JSON.stringify(a.post) === JSON.stringify(b.post);
}

/** Keep a post that got no answer, marked as a resend. False when it couldn't be kept (no store, or the write was refused),
 *  checked on the new entry itself, so an older kept post can't make a lost one look kept. */
export function addToOutbox(store: OutboxStore | null, post: any, now: number): boolean {
  if (!store || !post || typeof post !== 'object') { return false; }
  const meta = post.meta && typeof post.meta === 'object' ? post.meta : {};
  const entry: OutboxItem = { post: { ...post, meta: { ...meta, resent: 1 } }, queuedAt: now, tries: 0 };
  const items = readOutbox(store);
  items.push(entry);
  if (!writeOutbox(store, items.slice(-ACTION_OUTBOX_MAX_ITEMS))) { return false; }
  return readOutbox(store).some((item) => sameOutboxItem(item, entry));
}

/** Drop one kept post (one confirmed sent), leaving the rest. False when the store refused the change, so the post may be
 *  sent again later; that resend carries meta.resent. */
export function removeFromOutbox(store: OutboxStore | null, entry: OutboxItem): boolean {
  const items = readOutbox(store);
  const rest = items.filter((item) => !sameOutboxItem(item, entry));
  return rest.length === items.length ? true : writeOutbox(store, rest);
}

/** Resolves the HTTP status of one send: 2xx sent, 0 no answer, anything else a real error. */
export type OutboxSend = (post: any) => Promise<number>;

export interface OutboxFlushResult {
  sent: number;
  kept: number;
  gaveUp: OutboxItem[];
  rejected: { item: OutboxItem, status: number }[];
  /** False when the store refused to save what's left: the outbox was cleared and the unsent posts are in gaveUp. */
  saved: boolean;
}

/**
 * Send the kept posts in order. A sent one is dropped. One that gets no answer stays (and the ones after it wait without a send,
 * since the connection is still down) until it has had ACTION_OUTBOX_MAX_TRIES sends or waited ACTION_OUTBOX_MAX_AGE_MS; then it's
 * given up. One the server answers with an error is dropped as rejected. The caller reports the given-up and rejected ones.
 */
export async function flushOutbox(store: OutboxStore | null, send: OutboxSend, now: number): Promise<OutboxFlushResult> {
  const result: OutboxFlushResult = { sent: 0, kept: 0, gaveUp: [], rejected: [], saved: true };
  const items = readOutbox(store);
  if (!items.length) { return result; }
  const keep: OutboxItem[] = [];
  let down = false;
  for (const item of items) {
    if (down) { keep.push(item); continue; }
    let status: number;
    try { status = await send(item.post); } catch (e) { status = 0; }
    if (status >= 200 && status < 300) {
      result.sent++;
    } else if (status === 0) {
      const tried = { ...item, tries: item.tries + 1 };
      if (tried.tries >= ACTION_OUTBOX_MAX_TRIES || now - item.queuedAt >= ACTION_OUTBOX_MAX_AGE_MS) {
        result.gaveUp.push(tried);
      } else {
        keep.push(tried);
        down = true;
      }
    } else {
      result.rejected.push({ item, status });
    }
  }
  // A post kept by a new failure while this flush ran is carried over, after the ones still waiting here.
  const arrived = readOutbox(store).filter((item) => !items.some((old) => sameOutboxItem(old, item)));
  const next = keep.concat(arrived).slice(-ACTION_OUTBOX_MAX_ITEMS);
  if (writeOutbox(store, next)) {
    result.kept = keep.length;
    return result;
  }
  // The store refused the update. Leaving the old list would send the posts that just went through again, so the outbox is
  // cleared, and the posts still unsent are given up (and reported) rather than lost quietly.
  try { if (store) { store.removeItem(ACTION_OUTBOX_KEY); } } catch (e) { /* nothing more to do */ }
  result.saved = false;
  result.gaveUp = result.gaveUp.concat(next);
  result.kept = 0;
  return result;
}
